import React, { useState, useEffect } from "react";
import { ArrowRight, ArrowUpRight, LockKeyhole, Mail } from "lucide-react";
import { Brand, Button, Field } from "./ui";
import { supabase, demo, api, config, rpc } from "./data";
export function Auth({ onUser, route, notify }) {
  const [mode, setMode] = useState(
      route === "#reset-password" ? "reset" : "login",
    ),
    [form, setForm] = useState({
      email: "",
      password: "",
      phone: "",
      name: "",
    }),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState(""),
    [error, setError] = useState("");
  const set = (k, v) => setForm({ ...form, [k]: v });
  useEffect(() => {
    if (route === "#reset-password") setMode("reset");
  }, [route]);
  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    setMessage("");
    try {
      if (demo)
        throw new Error(
          "Conecta Supabase en config.js para activar el acceso real. Puedes explorar la demostración.",
        );
      let result;
      if (mode === "register")
        result = await supabase.auth.signUp({
          email: form.email,
          password: form.password,
          options: {
            data: { phone: form.phone, name: form.name },
            emailRedirectTo: `${location.origin}/config#login`,
          },
        });
      else if (mode === "forgot")
        result = await supabase.auth.resetPasswordForEmail(form.email, {
          redirectTo: `${location.origin}/config#reset-password`,
        });
      else if (mode === "reset")
        result = await supabase.auth.updateUser({ password: form.password });
      else
        result = await supabase.auth.signInWithPassword({
          email: form.email,
          password: form.password,
        });
      if (result.error) throw result.error;
      if (mode === "forgot")
        setMessage(
          "Si el correo está registrado, recibirás un enlace para recuperar tu contraseña.",
        );
      else if (mode === "register" && !result.data.session)
        setMessage(
          "Revisa tu correo para confirmar tu cuenta. Después inicia sesión.",
        );
      else {
        if (mode === "reset") notify("Contraseña actualizada");
        location.hash = "#admin/dashboard";
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="auth-layout">
      <section className="auth-art">
        <Brand />
        <span className="eyebrow">TU NEGOCIO, A TU MANERA</span>
        <h1>
          Todo lo que vendes.
          <br />
          <em>En un solo lugar.</em>
        </h1>
        <p>
          Tu tienda, tus productos y cada nueva venta.
          <br />
          Haz que las buenas ideas se conviertan en pedidos.
        </p>
        <img src="/assets/campana.png" alt="Dilo Shop" />
        <span className="auth-star">✦</span>
      </section>
      <section className="auth-form">
        <a href="/" className="back-store">
          Ver tienda <ArrowUpRight size={17} />
        </a>
        <div>
          <span className="eyebrow">BIENVENIDO A DILO SHOP</span>
          <h2>
            {mode === "register"
              ? "Crea tu cuenta"
              : mode === "forgot"
                ? "Recupera tu contraseña"
                : mode === "reset"
                  ? "Tu nueva contraseña"
                  : "Tu tienda te espera."}
          </h2>
          <p>
            {mode === "register"
              ? "Registra tus datos para administrar tu tienda."
              : mode === "forgot"
                ? "Te enviaremos un enlace a tu correo."
                : "Ingresa para continuar con tu negocio."}
          </p>
          <form onSubmit={submit}>
            {mode === "register" && (
              <Field
                label="Nombre completo"
                required
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
              />
            )}
            {mode !== "reset" && (
              <Field
                label="Correo electrónico"
                type="email"
                autoComplete="email"
                required
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
              />
            )}
            {mode === "register" && (
              <Field
                label="Número de celular (obligatorio)"
                type="tel"
                required
                pattern="[+0-9 ()\-]{7,20}"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                placeholder="+51 999 999 999"
              />
            )}
            {mode !== "forgot" && (
              <Field
                label="Contraseña"
                type="password"
                required
                minLength={10}
                autoComplete={
                  mode === "login" ? "current-password" : "new-password"
                }
                value={form.password}
                onChange={(e) => set("password", e.target.value)}
                hint={mode !== "login" ? "Mínimo 10 caracteres." : ""}
              />
            )}
            {mode === "login" && (
              <button
                className="text-button right"
                type="button"
                onClick={() => {
                  setMode("forgot");
                  setError("");
                }}
              >
                Olvidé mi contraseña
              </button>
            )}
            {error && (
              <p className="error-text" role="alert">
                {error}
              </p>
            )}
            {message && (
              <p className="notice" role="status">
                {message}
              </p>
            )}
            <Button className="full" type="submit" disabled={busy}>
              {busy
                ? "Un momento…"
                : mode === "register"
                  ? "Crear cuenta"
                  : mode === "forgot"
                    ? "Enviar enlace"
                    : mode === "reset"
                      ? "Guardar contraseña"
                      : "Iniciar sesión"}
              <ArrowRight size={18} />
            </Button>
          </form>
          <p className="auth-switch">
            {mode === "login" ? "¿Primera vez aquí? " : ""}
            <button
              onClick={() => {
                setMode(mode === "login" ? "register" : "login");
                setError("");
                setMessage("");
              }}
            >
              {mode === "login"
                ? "Crear mi cuenta"
                : "Volver al inicio de sesión"}
            </button>
          </p>
          {demo && (
            <div className="demo-entry">
              <p>Vista de demostración · sin conexión a Supabase</p>
              <Button
                kind="outline"
                className="full"
                onClick={() => {
                  sessionStorage.setItem("dilo-demo-admin", "yes");
                  onUser({ id: "demo-owner", email: "demo@dilo.local" });
                  location.hash = "#admin/dashboard";
                }}
              >
                Explorar panel de demostración <ArrowUpRight size={17} />
              </Button>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
